import {
  APIKeysSectionRoute,
  AccountSettingsRoute,
  GeneralSectionRoute,
  PaymentMethodsSectionRoute,
  UsersSectionRoute,
} from 'pages/account/route';
import { CryptoPayoutCreateRoute, CryptoPayoutsListRoute } from 'pages/cryptoPayout/route';
import { CryptoPayoutViewRoute } from 'pages/cryptoPayout/view';
import { DocumentationRoute } from 'pages/documentation/route';
import {
  MerchantGeneralSectionRoute,
  MerchantPayoutReserveRoute,
  MerchantRefundAccountRoute,
  MerchantRefundAccountTransactionRoute,
  MerchantViewRoute,
  MerchantsListRoute,
  SettlementSectionRoute,
} from 'pages/merchant/route';
import { NotImplementedRoute } from 'pages/notImplemented/route';
import { OrderViewRoute } from 'pages/orders/route';
import PaymentByLinkRoute from 'pages/paymentByLink/route';
import { PayoutViewRoute } from 'pages/payout/route';
import { ProfileRoute } from 'pages/profile/route';
import { PwcCustomerViewRoute, PwcCustomersListRoute } from 'pages/pwcCustomers/route';
import { PwcOrderListRoute, PwcOrderViewRoute } from 'pages/pwcOrders/route';

import RoutePath from 'router/path';

import { AccountAccessLevel, AccountType } from 'types/account';
import { AvailableRoute } from 'types/routing';

import { PartnerFeatures, canManageAccount } from './features';
import { getPartnerAccountSettingsChildPaths, shouldRegisterPartnerAccountSettings } from './pwcAccountSettings';

const getCommonRoutes = (): AvailableRoute[] => [
  {
    path: RoutePath.Documentation.Root,
    component: DocumentationRoute,
  },
  {
    path: RoutePath.Profile.Root,
    component: ProfileRoute,
  },
];

const accountSettingsChildComponents: Record<string, AvailableRoute['component']> = {
  [RoutePath.Pwc.Accounts.General]: MerchantGeneralSectionRoute,
  [RoutePath.Pwc.AccountSettings.Users]: UsersSectionRoute,
  [RoutePath.Pwc.AccountSettings.APIKeys]: APIKeysSectionRoute,
  [RoutePath.Pwc.AccountSettings.Settlement]: SettlementSectionRoute,
  [RoutePath.Pwc.AccountSettings.RefundAccount]: MerchantRefundAccountRoute,
  [RoutePath.Pwc.AccountSettings.PayoutReserve]: MerchantPayoutReserveRoute,
};

const getAccountSettingsChildren = (features: PartnerFeatures): AvailableRoute[] =>
  getPartnerAccountSettingsChildPaths(features).map((path) => ({
    path,
    component: accountSettingsChildComponents[path],
  }));

const getPwcRoutes = (accountType: AccountType, accessLevel: AccountAccessLevel, features: PartnerFeatures): AvailableRoute[] => {
  const routes: AvailableRoute[] = [];
  const isGroupOrPsp = accountType === AccountType.Pwc_Group || accountType === AccountType.PSP;
  const canManage = canManageAccount(accessLevel);

  if (features.pwc) {
    routes.push(
      {
        path: RoutePath.Pwc.Orders.Root,
        component: PwcOrderListRoute,
      },
      {
        path: RoutePath.Pwc.Orders.ById,
        component: PwcOrderViewRoute,
      },
      {
        path: RoutePath.Pwc.Orders.ReceiptById,
        component: NotImplementedRoute,
      },
      {
        path: RoutePath.Pwc.Payouts.ById,
        component: PayoutViewRoute,
      },
      {
        path: RoutePath.Pwc.Accounts.Root,
        component: MerchantsListRoute,
      },
    );

    if (isGroupOrPsp) {
      routes.push(
        {
          path: RoutePath.Pwc.Customers.Root,
          component: PwcCustomersListRoute,
        },
        {
          path: RoutePath.Pwc.Customers.ById,
          component: PwcCustomerViewRoute,
        },
      );
    }

    const merchantViewChildren: AvailableRoute[] = [
      {
        path: RoutePath.Pwc.Accounts.General,
        component: MerchantGeneralSectionRoute,
      },
      {
        path: RoutePath.Pwc.AccountSettings.Users,
        component: UsersSectionRoute,
      },
      {
        path: RoutePath.Pwc.AccountSettings.RefundAccount,
        component: MerchantRefundAccountRoute,
      },
      {
        path: RoutePath.Pwc.AccountSettings.PayoutReserve,
        component: MerchantPayoutReserveRoute,
      },
    ];

    if (canManage) {
      merchantViewChildren.push(
        {
          path: RoutePath.Pwc.AccountSettings.APIKeys,
          component: APIKeysSectionRoute,
        },
        {
          path: RoutePath.Pwc.AccountSettings.Settlement,
          component: SettlementSectionRoute,
        },
      );
    }

    routes.push({
      path: RoutePath.Pwc.Accounts.ById,
      component: MerchantViewRoute,
      defaultRedirectRoute: RoutePath.Pwc.Accounts.General,
      children: merchantViewChildren,
    });

    if (canManage) {
      routes.push(
        {
          path: RoutePath.Pwc.PaymentByLink.Root,
          component: PaymentByLinkRoute,
        },
        {
          path: RoutePath.Pwc.RefundAccountTransactions.ById,
          component: MerchantRefundAccountTransactionRoute,
          defaultRedirectRoute: RoutePath.Pwc.AccountSettings.General,
        },
      );
    }
  }

  if (shouldRegisterPartnerAccountSettings(accountType, accessLevel)) {
    routes.push({
      path: RoutePath.Pwc.AccountSettings.Root,
      component: MerchantViewRoute,
      defaultRedirectRoute: RoutePath.Pwc.AccountSettings.General,
      children: getAccountSettingsChildren(features),
    });
  }

  if (features.cryptoPayout) {
    routes.push(
      {
        path: RoutePath.CryptoPayout.Payouts.Root,
        component: CryptoPayoutsListRoute,
      },
      {
        path: RoutePath.CryptoPayout.Payouts.ById,
        component: CryptoPayoutViewRoute,
      },
      {
        path: RoutePath.CryptoPayout.PayoutByLink.Root,
        component: CryptoPayoutCreateRoute,
      },
    );
  }

  return [...routes, ...getCommonRoutes()];
};

const getRampRoutes = (accessLevel: AccountAccessLevel): AvailableRoute[] => {
  const routes: AvailableRoute[] = [
    {
      path: RoutePath.Ramp.Orders.Root,
      component: NotImplementedRoute,
    },
    {
      path: RoutePath.Ramp.Orders.ById,
      component: OrderViewRoute,
    },
  ];

  if (canManageAccount(accessLevel)) {
    routes.push({
      path: RoutePath.AccountSettings.Root,
      component: AccountSettingsRoute,
      defaultRedirectRoute: RoutePath.AccountSettings.General,
      children: [
        {
          path: RoutePath.AccountSettings.General,
          component: GeneralSectionRoute,
        },
        {
          path: RoutePath.AccountSettings.PaymentMethods,
          component: PaymentMethodsSectionRoute,
        },
        {
          path: RoutePath.AccountSettings.APIKeys,
          component: APIKeysSectionRoute,
        },
        {
          path: RoutePath.AccountSettings.Users,
          component: UsersSectionRoute,
        },
      ],
    });
  }

  return [...routes, ...getCommonRoutes()];
};

export const getAvailableRoutes = (
  accountType: AccountType | undefined,
  accessLevel: AccountAccessLevel,
  features: PartnerFeatures,
): AvailableRoute[] => {
  switch (accountType) {
    case AccountType.Ramp:
      return getRampRoutes(accessLevel);
    case AccountType.Pwc:
    case AccountType.Pwc_Group:
    case AccountType.PSP:
      return getPwcRoutes(accountType, accessLevel, features);
    default:
      return getCommonRoutes();
  }
};

export default getAvailableRoutes;
